@file:Suppress("MagicNumber")

package com.nonopn.skydome.common.app.navigation

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color

import com.nonopn.skydome.common.app.home.AppModel
import com.nonopn.skydome.common.app.home.LocalApp
import com.nonopn.skydome.common.app.pages.certnsg.CertNsgScreen
import com.nonopn.skydome.common.app.pages.certnsg.ZoneStats
import com.nonopn.skydome.common.app.pages.keraunic.ApiKeraunicEvents
import com.nonopn.skydome.common.app.pages.main.model.api.Project
import com.nonopn.skydome.common.app.pages.sharing.createShareManager
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.menu_cert_nsg
import com.nonopn.skywatcher.design.appbar.AppBarState
import com.nonopn.skywatcher.design.widgets.MenuItem
import com.nonopn.skywatcher.design.widgets.endOfDay
import com.nonopn.skywatcher.design.widgets.startOfDay
import com.nonopn.skywatcher.maps.LatLng

import korlibs.time.DateTime
import korlibs.time.hours

import eu.codlab.navigation.NavTypeWrapper
import eu.codlab.navigation.NavigateTo
import eu.codlab.navigation.NavigateToStack
import eu.codlab.navigation.NavigateWithNavOptions
import eu.codlab.navigation.Route
import eu.codlab.navigation.RouteParameterTo
import eu.codlab.navigation.RouterParameters

import kotlinx.serialization.Serializable

import kotlin.reflect.KType

private object Colors {
    val Primary = Color(0xFF2B7FE0)
    val Muted = Color(0xFF6B7A99)
}

@Serializable
data class RouteCertNsg(
    val projectUuid: String,
    val projectName: String,
    val lat: Double,
    val lon: Double,
) : RouteParameterTo

object RouterCertNsg : RouterParameters<RouteCertNsg>(RouteCertNsg::class) {
    override val typeMap: Map<KType, NavTypeWrapper<Any>> get() = emptyMap()

    override fun routeFrom(parameters: RouteCertNsg) = "/certificate/nsg/${parameters.projectUuid}"

    @Composable
    override fun Show(parameters: RouteCertNsg) {
        RouteCertNsgImpl(parameters).scene()
    }

    override fun navigateTo(instance: RouteCertNsg) = NavigateTo(
        route = instance,
        stack = NavigateToStack(
            popBackStack = false,
            options = NavigateWithNavOptions(launchSingleTop = true)
        )
    )

    override fun isCurrentRoute(routeParameterTo: RouteParameterTo?) =
        routeParameterTo is RouteCertNsg

    override fun isMatching(route: String) = route.startsWith("/certificate/nsg/")

    override fun navigateFrom(path: String): RouteCertNsg = TODO()
}

class RouteCertNsgImpl(params: RouteCertNsg) : Route<RouteCertNsg>(
    route = "/certificate/nsg/${params.projectUuid}",
    params = params
) {
    @Composable
    override fun scene() {
        val appModel: AppModel = LocalApp.current
        val state by appModel.states.collectAsState()

        LaunchedEffect(Unit) { onEntryIsActive(appModel) }

        val project = remember {
            Project(
                latitude = params.lat,
                longitude = params.lon,
                name = params.projectName,
                uuid = params.projectUuid
            )
        }

        // Fetch Nsg value + zone stats from GLD360+ network
        var nsg12Months by remember { mutableStateOf<Double?>(null) }
        var zone1Stats by remember { mutableStateOf<ZoneStats?>(null) }
        var zone2Stats by remember { mutableStateOf<ZoneStats?>(null) }
        var analysisPeriod by remember { mutableStateOf("") }
        var isLoading by remember { mutableStateOf(true) }
        var errorMessage by remember { mutableStateOf<String?>(null) }

        LaunchedEffect(Unit) {
            val api = state.apiProvider?.apiNormal
            if (api == null) {
                errorMessage = "API not available"
                isLoading = false
                return@LaunchedEffect
            }
            try {
                val coordinates = LatLng(params.lat, params.lon)
                val result = api.ng(coordinates)
                nsg12Months = result.ng1year

                // Fetch zone stats from keraunicDetail
                try {
                    val from = (DateTime.now().local.startOfDay - (365 * 24).hours)
                    val to = DateTime.now().local.endOfDay
                    analysisPeriod = formatPeriod(from, to)
                    val detail = api.keraunicDetail(coordinates, from, to)
                    zone1Stats = buildZoneStats(detail.eventsAt1Km)
                    zone2Stats = buildZoneStats(detail.eventsAt2Km)
                } catch (_: Throwable) {
                    // Zone stats are optional — screen works without them
                }
            } catch (e: Exception) {
                errorMessage = "${e::class.simpleName}: ${e.message}"
            } finally {
                isLoading = false
            }
        }

        when {
            isLoading -> Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                CircularProgressIndicator(color = Colors.Primary)
            }

            nsg12Months != null -> CertNsgScreen(
                modifier = Modifier.fillMaxSize(),
                siteName = project.name,
                nsg12Months = nsg12Months!!,
                analysisPeriod = analysisPeriod,
                shareManager = createShareManager(),
                appModel = appModel,
                projectUuid = project.uuid,
                latitude = project.latitude,
                longitude = project.longitude,
                zone1Stats = zone1Stats,
                zone2Stats = zone2Stats,
            )

            else -> Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = errorMessage ?: "Unable to load Nsg data. Please try again.",
                    color = Colors.Muted
                )
            }
        }
    }

    private fun onEntryIsActive(appModel: AppModel) {
        appModel.setAppBarState(
            AppBarState.Localized(
                title = Res.string.menu_cert_nsg,
                emptyList<MenuItem>()
            )
        )
    }
}

/**
 * Build [ZoneStats] from aggregate API data (pos + neg combined).
 * Amplitude values are stored in amps; converted to kA (÷ 1000).
 */
private fun buildZoneStats(events: ApiKeraunicEvents): ZoneStats? {
    val totalCount = events.pos.count + events.negs.count
    if (totalCount == 0) return null

    val posCount = events.pos.count
    val negCount = events.negs.count

    val maxKa = maxOf(
        kotlin.math.abs(events.pos.max),
        kotlin.math.abs(events.negs.max)
    ).toDouble() / 1000.0

    val avgKa = if (totalCount > 0) {
        (kotlin.math.abs(events.pos.avg) * posCount +
            kotlin.math.abs(events.negs.avg) * negCount) / totalCount / 1000.0
    } else 0.0

    // Approximate median: weighted by count
    val medianKa = if (posCount > 0 && negCount > 0) {
        val w = posCount.toDouble() / totalCount
        (kotlin.math.abs(events.pos.median) * w +
            kotlin.math.abs(events.negs.median) * (1 - w)) / 1000.0
    } else if (posCount > 0) {
        kotlin.math.abs(events.pos.median).toDouble() / 1000.0
    } else {
        kotlin.math.abs(events.negs.median).toDouble() / 1000.0
    }

    return ZoneStats(
        count = totalCount,
        avgKa = avgKa,
        medianKa = medianKa,
        maxKa = maxKa,
    )
}

private val MONTH_NAMES_FR = arrayOf(
    "jan.", "fév.", "mars", "avr.", "mai", "juin",
    "juil.", "août", "sept.", "oct.", "nov.", "déc."
)

private fun formatPeriod(
    from: korlibs.time.DateTimeTz,
    to: korlibs.time.DateTimeTz
): String {
    val f = MONTH_NAMES_FR[from.month1 - 1]
    val t = MONTH_NAMES_FR[to.month1 - 1]
    return "$f ${from.yearInt} – $t ${to.yearInt}"
}
